import { Component, Inject, OnInit, Optional } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Observable } from 'rxjs';
import { ContactBankAccountModel } from './contactbankaccount.model';
import { ContactBankAccountService } from './contactbankaccount.service';
import { ContactBankAccountFormComponent } from './contactbankaccount-form.component';
import { ConfirmService } from '../../../modules_v2/shared/services/confirm.service';
import { ConfirmDialogDetail } from '../../../modules_v2/shared/components/confirm-dialog.component';
import { NotificationService } from '../../../modules_v2/shared/services/notification.service';

export interface ContactBankAccountListDialogData {
  uid: string;
}

@Component({
    selector: 'convfact-contactbankaccount-list',
    templateUrl: './contactbankaccount-list.component.html',
    styleUrls: ['./contactbankaccount.component.css'],
    standalone: false
})
export class ContactBankAccountListComponent implements OnInit {
  contactUid: string;
  dataSource: ContactBankAccountModel[] = [];
  ready = false;
  loadError = false;
  busyUid: string | null = null;

  static open(dialog: MatDialog, data: ContactBankAccountListDialogData): Observable<void> {
    return dialog.open<ContactBankAccountListComponent, ContactBankAccountListDialogData>(ContactBankAccountListComponent, {
      width: '640px',
      maxWidth: '96vw',
      maxHeight: '90vh',
      autoFocus: false,
      data,
    }).afterClosed();
  }

  constructor(
    private contactBankAccountService: ContactBankAccountService,
    private route: ActivatedRoute,
    private confirm: ConfirmService,
    private notify: NotificationService,
    public dialog: MatDialog,
    @Optional() public dialogRef: MatDialogRef<ContactBankAccountListComponent>,
    @Optional() @Inject(MAT_DIALOG_DATA) public data: ContactBankAccountListDialogData,
  ) {
    this.contactUid = data?.uid || this.route.parent?.snapshot.paramMap.get('contactUid') || this.route.snapshot.paramMap.get('contactUid');
  }

  get isDialog(): boolean {
    return !!this.dialogRef;
  }

  get empty(): boolean {
    return this.dataSource.length === 0;
  }

  ngOnInit(): void {
    this.load();
  }

  load(): void {
    if (!this.contactUid) {
      this.loadError = true;
      this.ready = true;
      return;
    }
    this.ready = false;
    this.loadError = false;
    this.contactBankAccountService.getList(this.contactUid)
      .then(data => { this.dataSource = Array.isArray(data) ? data : []; })
      .catch(() => { this.loadError = true; })
      .then(() => { this.ready = true; });
  }

  create(): void {
    ContactBankAccountFormComponent.open(this.dialog, { uid: this.contactUid })
      .subscribe(changed => { if (changed) this.load(); });
  }

  edit(item: ContactBankAccountModel): void {
    if (this.busyUid) return;
    ContactBankAccountFormComponent.open(this.dialog, { uid: this.contactUid, bankAccountUid: item.uid })
      .subscribe(changed => { if (changed) this.load(); });
  }

  setRemesa(item: ContactBankAccountModel): void {
    if (this.busyUid) return;
    this.busyUid = item.uid;
    const remesa = !item.remesa;
    this.contactBankAccountService.loadData(this.contactUid, item.uid)
      .then(bankAccount => {
        bankAccount.remesa = remesa;
        return this.contactBankAccountService.editData(this.contactUid, item.uid, bankAccount);
      })
      .then(() => {
        this.notify.ok(remesa ? 'Cuenta marcada como principal' : 'Cuenta desmarcada como principal');
        this.busyUid = null;
        this.load();
      })
      .catch(error => {
        this.busyUid = null;
        this.notify.error(error?.error?.message || 'No se pudo actualizar la cuenta bancaria');
      });
  }

  delete(item: ContactBankAccountModel): void {
    if (this.busyUid) return;
    this.confirm.open({
      title: 'Eliminar cuenta bancaria',
      message: 'Se eliminará la cuenta bancaria del contacto. Esta acción no se puede deshacer.',
      tone: 'danger',
      details: this.details(item),
      confirmText: 'Eliminar',
      confirmIcon: 'delete',
      loadingText: 'Eliminando cuenta bancaria…',
      errorText: 'No se pudo eliminar la cuenta bancaria',
      action: () => this.contactBankAccountService.deleteData(this.contactUid, item.uid),
    }).subscribe(deleted => {
      if (!deleted) return;
      this.notify.ok('Cuenta bancaria eliminada');
      this.load();
    });
  }

  close(): void {
    this.dialogRef?.close();
  }

  formatIban(iban: string): string {
    const clean = (iban || '').replace(/\s+/g, '');
    return clean.replace(/(.{4})(?=.)/g, '$1 ');
  }

  trackByUid(_: number, item: ContactBankAccountModel): string {
    return item.uid;
  }

  private details(item: ContactBankAccountModel): ConfirmDialogDetail[] {
    const details: ConfirmDialogDetail[] = [{ label: 'IBAN', value: this.formatIban(item.iban) }];
    if (item.bank) details.push({ label: 'Entidad', value: item.bank });
    if (item.swift) details.push({ label: 'BIC/SWIFT', value: item.swift });
    return details;
  }
}
